import { useState, useEffect } from "react";
import { SIMON_BUTTONS } from "./SimonHost";

export default function SimonPlayer({ simonData, playerName, onPress }) {
  const phase      = simonData?.phase ?? "waiting";
  const sequence   = simonData?.sequence ?? [];
  const eliminated = simonData?.eliminated ?? [];
  const alive      = simonData?.alive ?? [];
  const failed     = simonData?.failed ?? [];
  const myProgress = simonData?.progress?.[playerName] ?? 0; // how many buttons I've entered correctly
  const scores     = simonData?.scores ?? {};

  const isElim    = eliminated.includes(playerName);
  const justFailed = failed.includes(playerName);
  const myScore   = scores[playerName] ?? 0;

  // Which button index I need to press next
  const nextIdx = myProgress;
  const done    = myProgress >= sequence.length;

  // Prevent double-press: track last pressed to show flash
  const [flashBtn, setFlashBtn] = useState(null);

  function handlePress(btnId) {
    if (phase !== "input") return;
    if (isElim) return;
    if (done) return;
    setFlashBtn(btnId);
    setTimeout(() => setFlashBtn(null), 150);
    onPress(btnId);
  }

  // ── ELIMINATED ────────────────────────────────────────────────────────────
  if (isElim) {
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: "0.75rem" }}>💀</div>
        <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#FF3355" }}>
          Ты вылетел!
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize:"var(--fs-sm)", marginTop: "0.5rem" }}>
          Смотри как остальные запоминают
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)", marginTop: "1rem" }}>
          Итого: <span style={{ color: "#ffe600" }}>{myScore}</span> очков
        </p>
      </div>
    );
  }

  // ── SHOW PHASE — watch the sequence ──────────────────────────────────────
  if (phase === "show") {
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize:"var(--fs-3xl)", marginBottom: "0.75rem" }}>👀</div>
        <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#fff" }}>
          Запоминай последовательность!
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize:"var(--fs-sm)", marginTop: "0.5rem" }}>
          {sequence.length} {sequence.length === 1 ? "кнопка" : sequence.length < 5 ? "кнопки" : "кнопок"}
        </p>
      </div>
    );
  }

  // ── RESULT PHASE ─────────────────────────────────────────────────────────
  if (phase === "result") {
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>
          {justFailed ? "❌" : "✅"}
        </div>
        <p style={{
          fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)",
          color: justFailed ? "#FF3355" : "#3dffa0",
        }}>
          {justFailed ? "Ошибка!" : "Правильно!"}
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)", marginTop: "1rem" }}>
          Очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
        </p>
      </div>
    );
  }

  // ── INPUT PHASE — press buttons ───────────────────────────────────────────
  if (phase === "input") {
    return (
      <div style={{ padding: "1rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
        {/* Progress indicator */}
        <div style={{
          fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-md)",
          color: done ? "#3dffa0" : "#fff", textAlign: "center",
        }}>
          {done
            ? "✅ Готово! Ждём остальных..."
            : `Кнопка ${myProgress + 1} из ${sequence.length}`}
        </div>

        {/* Dot progress */}
        <div style={{ display: "flex", gap:"var(--gap-sm)" }}>
          {sequence.map((_, i) => (
            <div key={i} style={{
              width: "12px", height: "12px", borderRadius: "50%",
              background: i < myProgress ? "#3dffa0" : i === myProgress ? "#ffe600" : "#333",
              border: i === myProgress ? "2px solid #ffe600" : "2px solid #444",
              transition: "all 0.15s",
            }} />
          ))}
        </div>

        {/* 4 buttons */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem", width: "100%", maxWidth: "320px",
        }}>
          {SIMON_BUTTONS.map(btn => {
            const isFlash = flashBtn === btn.id;
            return (
              <button
                key={btn.id}
                onPointerDown={() => handlePress(btn.id)}
                disabled={done}
                style={{
                  background: isFlash ? btn.color : btn.color + "44",
                  border: `3px solid ${btn.color}`,
                  borderRadius: "1rem",
                  height: "90px",
                  fontSize:"var(--fs-3xl)",
                  cursor: done ? "default" : "pointer",
                  boxShadow: isFlash ? `0 0 30px ${btn.glow}` : "none",
                  transform: isFlash ? "scale(0.93)" : "scale(1)",
                  transition: "all 0.1s",
                  touchAction: "manipulation",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }}
              >
                {btn.label}
              </button>
            );
          })}
        </div>

        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)" }}>
          Очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
        </p>
      </div>
    );
  }

  // ── WAITING ───────────────────────────────────────────────────────────────
  return (
    <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
      <div style={{ fontSize:"var(--fs-3xl)", marginBottom: "0.75rem" }}>⚡</div>
      <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#9b5de5" }}>
        Повтори комбо
      </p>
      <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize:"var(--fs-sm)", marginTop: "0.5rem" }}>
        Жди начала раунда
      </p>
    </div>
  );
}
